import React, { useEffect, useMemo, useState } from 'react'
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  FolderIcon,
  DocumentTextIcon,
  PlayCircleIcon,
  QuestionMarkCircleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import { AdminApi, AdminCourse, AdminCourseContent } from '../../services/adminApi'
import { toast } from 'react-toastify'

interface CourseContentCopyFromModalProps {
  isOpen: boolean
  onClose: () => void
  onCopied: () => void
  courseId: number
  targetParentId: number | null
}

const isFolderType = (type?: string | null) => {
  const t = String(type || '').toUpperCase()
  return t === 'FORDER' || t === 'FOLDER'
}

const normalizeParentId = (value: unknown): number | null => {
  if (value == null || value === '' || value === 0 || value === '0') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

const normalizeContentItem = (raw: any): AdminCourseContent => {
  const contentTypeRaw = String(raw.contentType ?? raw.content_type ?? 'POST').toUpperCase()
  let contentType = contentTypeRaw as AdminCourseContent['contentType']
  if (contentTypeRaw === 'FOLDER') contentType = 'FORDER'
  if (contentTypeRaw === 'QUIZ' || contentTypeRaw === 'QUIZZ') contentType = 'TEST'

  return {
    id: Number(raw.id),
    hashId: raw.hashId ?? raw.hash_id,
    courseId: Number(raw.courseId ?? raw.course_id),
    contentType,
    name: raw.name,
    detail: raw.detail ?? undefined,
    objectId:
      raw.objectId != null
        ? Number(raw.objectId)
        : raw.object_id != null
          ? Number(raw.object_id)
          : null,
    parentId: normalizeParentId(raw.parentId ?? raw.parent_id),
    position: raw.position != null ? Number(raw.position) : 0,
    isPublic: raw.isPublic ?? raw.is_public,
    status: raw.status,
  }
}

const getIcon = (type?: string) => {
  const t = String(type || '').toUpperCase()
  if (t === 'FORDER' || t === 'FOLDER') return <FolderIcon className="h-4 w-4 text-amber-500 shrink-0" />
  if (t === 'VIDEO') return <PlayCircleIcon className="h-4 w-4 text-m2-primary shrink-0" />
  if (t === 'TEST' || t === 'QUIZ' || t === 'QUIZZ') {
    return <QuestionMarkCircleIcon className="h-4 w-4 text-purple-500 shrink-0" />
  }
  return <DocumentTextIcon className="h-4 w-4 text-m2-on-surface-variant shrink-0" />
}

const CourseContentCopyFromModal: React.FC<CourseContentCopyFromModalProps> = ({
  isOpen,
  onClose,
  onCopied,
  courseId,
  targetParentId,
}) => {
  const [courses, setCourses] = useState<AdminCourse[]>([])
  const [courseQuery, setCourseQuery] = useState('')
  const [loadingCourses, setLoadingCourses] = useState(false)
  const [sourceCourseId, setSourceCourseId] = useState<number | null>(null)

  const [contentList, setContentList] = useState<AdminCourseContent[]>([])
  const [loadingContent, setLoadingContent] = useState(false)
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setSourceCourseId(null)
    setContentList([])
    setExpandedIds(new Set())
    setSelectedIds(new Set())
    setCourseQuery('')
    setIsSubmitting(false)

    let cancelled = false
    const loadCourses = async () => {
      setLoadingCourses(true)
      try {
        const res = await AdminApi.getCourses()
        if (cancelled) return
        if (res.success) {
          setCourses((res.data?.items || []).filter((c) => Number(c.id) !== courseId))
        }
      } catch (e) {
        console.error(e)
        toast.error('Không tải được danh sách khóa học')
      } finally {
        if (!cancelled) setLoadingCourses(false)
      }
    }
    void loadCourses()
    return () => {
      cancelled = true
    }
  }, [isOpen, courseId])

  useEffect(() => {
    if (!isOpen || sourceCourseId == null) {
      setContentList([])
      setExpandedIds(new Set())
      setSelectedIds(new Set())
      return
    }

    let cancelled = false
    const loadContent = async () => {
      setLoadingContent(true)
      setExpandedIds(new Set())
      setSelectedIds(new Set())
      try {
        const res = await AdminApi.getCourseContent(sourceCourseId)
        if (cancelled) return
        if (res.success) {
          setContentList((res.data || []).map(normalizeContentItem))
        } else {
          setContentList([])
          toast.error('Không tải được nội dung khóa nguồn')
        }
      } catch (e) {
        console.error(e)
        if (!cancelled) {
          setContentList([])
          toast.error('Không tải được nội dung khóa nguồn')
        }
      } finally {
        if (!cancelled) setLoadingContent(false)
      }
    }
    void loadContent()
    return () => {
      cancelled = true
    }
  }, [isOpen, sourceCourseId])

  const filteredCourses = useMemo(() => {
    const q = courseQuery.trim().toLowerCase()
    if (!q) return courses
    return courses.filter(
      (c) =>
        String(c.name || '')
          .toLowerCase()
          .includes(q) ||
        String(c.code || '')
          .toLowerCase()
          .includes(q) ||
        String(c.id).includes(q)
    )
  }, [courses, courseQuery])

  const getChildren = (parentId: number | null) =>
    contentList
      .filter((c) => normalizeParentId(c.parentId) === parentId)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id)

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSubmit = async () => {
    if (sourceCourseId == null) {
      toast.error('Vui lòng chọn khóa nguồn')
      return
    }
    if (selectedIds.size === 0) {
      toast.error('Vui lòng chọn ít nhất một thư mục hoặc bài')
      return
    }
    try {
      setIsSubmitting(true)
      const res = await AdminApi.copyCourseContentFrom(courseId, {
        sourceCourseId,
        targetParentId,
        itemIds: Array.from(selectedIds),
      })
      if (res.success) {
        toast.success(res.message || `Đã thêm ${res.data?.createdCount ?? selectedIds.size} nội dung`)
        onCopied()
        onClose()
      } else {
        toast.error(res.message || 'Copy nội dung thất bại')
      }
    } catch (err: any) {
      console.error(err)
      toast.error(err?.response?.data?.message || err?.message || 'Copy nội dung thất bại')
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderTreeItem = (item: AdminCourseContent, depth: number) => {
    const isFolder = isFolderType(item.contentType)
    const children = isFolder ? getChildren(item.id) : []
    const expanded = expandedIds.has(item.id)
    const selected = selectedIds.has(item.id)

    return (
      <div key={item.id}>
        <div
          className={`flex items-center gap-1.5 py-1.5 pr-2 hover:bg-m2-primary/10 ${
            selected ? 'bg-m2-primary/10' : ''
          }`}
          style={{ paddingLeft: `${8 + depth * 16}px` }}
        >
          {isFolder ? (
            <button
              type="button"
              onClick={() => toggleExpand(item.id)}
              className="p-0.5 text-m2-on-surface-variant hover:text-m2-on-surface rounded"
              title={expanded ? 'Thu gọn' : 'Mở rộng'}
            >
              {expanded ? (
                <ChevronDownIcon className="h-3.5 w-3.5" />
              ) : (
                <ChevronRightIcon className="h-3.5 w-3.5" />
              )}
            </button>
          ) : (
            <span className="w-4.5 inline-block" />
          )}
          <input
            type="checkbox"
            checked={selected}
            onChange={() => toggleSelect(item.id)}
            className="rounded border-m2-outline text-m2-primary focus:ring-m2-primary"
          />
          {getIcon(item.contentType)}
          <button
            type="button"
            className={`text-left text-sm truncate flex-1 ${
              isFolder ? 'font-medium text-m2-on-surface' : 'text-m2-on-surface-variant'
            }`}
            onClick={() => toggleSelect(item.id)}
          >
            {item.name}
          </button>
        </div>
        {isFolder && expanded && children.map((child) => renderTreeItem(child, depth + 1))}
      </div>
    )
  }

  if (!isOpen) return null

  const rootItems = getChildren(null)
  const selectedCount = selectedIds.size

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-m2-surface-container rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-m2-outline-variant">
          <div>
            <h2 className="text-xl font-semibold text-m2-on-surface">Thêm từ khóa khác</h2>
            <p className="text-xs text-m2-on-surface-variant mt-0.5">
              Chọn khóa nguồn, rồi chọn thư mục hoặc bài để thêm vào vị trí hiện tại
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-m2-on-surface-variant hover:text-m2-on-surface"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto space-y-4">
          <div>
            <label className="block text-sm font-medium text-m2-on-surface mb-1">
              Khóa nguồn <span className="text-red-500">*</span>
            </label>
            <div className="relative mb-2">
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-m2-outline" />
              <input
                type="text"
                className="w-full border border-m2-outline rounded-md pl-9 pr-3 py-2 text-sm"
                placeholder="Tìm khóa theo tên, mã, id..."
                value={courseQuery}
                onChange={(e) => setCourseQuery(e.target.value)}
              />
            </div>
            <div className="border border-m2-outline-variant rounded-md max-h-40 overflow-y-auto">
              {loadingCourses ? (
                <p className="p-3 text-sm text-m2-on-surface-variant">Đang tải khóa học...</p>
              ) : filteredCourses.length === 0 ? (
                <p className="p-3 text-sm text-m2-on-surface-variant">Không có khóa học phù hợp</p>
              ) : (
                filteredCourses.map((c) => {
                  const selected = sourceCourseId === c.id
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSourceCourseId(c.id)}
                      className={`w-full text-left px-3 py-2 text-sm border-b border-m2-outline-variant last:border-b-0 ${
                        selected
                          ? 'bg-m2-primary/15 text-m2-primary font-medium'
                          : 'hover:bg-m2-primary/10 text-m2-on-surface'
                      }`}
                    >
                      <span className="truncate block">
                        #{c.id} — {c.name}
                        {c.code ? ` (${c.code})` : ''}
                      </span>
                    </button>
                  )
                })
              )}
            </div>
          </div>

          {sourceCourseId != null && (
            <div>
              <label className="block text-sm font-medium text-m2-on-surface mb-1">
                Nội dung khóa nguồn
                {selectedCount > 0 && (
                  <span className="ml-2 text-xs font-normal text-m2-primary">
                    Đã chọn {selectedCount}
                  </span>
                )}
              </label>
              <div className="border border-m2-outline-variant rounded-md max-h-72 overflow-y-auto bg-m2-surface-low">
                {loadingContent ? (
                  <p className="p-3 text-sm text-m2-on-surface-variant">Đang tải nội dung...</p>
                ) : rootItems.length === 0 ? (
                  <p className="p-3 text-sm text-m2-on-surface-variant">
                    Khóa nguồn chưa có nội dung
                  </p>
                ) : (
                  rootItems.map((item) => renderTreeItem(item, 0))
                )}
              </div>
              <p className="text-xs text-m2-on-surface-variant mt-1.5">
                Thư mục đang thu gọn — bấm mũi tên để mở. Chọn thư mục sẽ copy cả nội dung bên trong.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-m2-outline-variant">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md border border-m2-outline-variant text-m2-on-surface hover:bg-m2-surface-high"
            disabled={isSubmitting}
          >
            Hủy
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || sourceCourseId == null || selectedCount === 0}
            className="px-4 py-2 text-sm rounded-md bg-m2-primary text-m2-on-primary hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Đang thêm...' : 'Thêm vào khóa học'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CourseContentCopyFromModal
